import {
  Button,
  Stack,
  HStack,
  Text,
  Box,
  Icon,
  useDisclosure,
  Separator,
  CheckboxGroup,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { PrinterIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';

interface FieldOption {
  id: string;
  label: string;
  type: string;
}

interface PrintFieldSelectorProps {
  fields: FieldOption[];
  onPrint: (selectedFields: string[]) => void;
  buttonText?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function PrintFieldSelector({
  fields,
  onPrint,
  buttonText = 'Print Options',
  open: openProp,
  onOpenChange,
}: PrintFieldSelectorProps) {
  const internal = useDisclosure();
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internal.open;
  const setOpen = (value: boolean) => {
    if (isControlled) {
      onOpenChange?.(value);
    } else if (value) {
      internal.onOpen();
    } else {
      internal.onClose();
    }
  };
  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  useEffect(() => {
    if (fields && fields.length > 0) {
      setSelectedFields(fields.map((field) => field.id));
    }
  }, [fields]);

  const handleToggleField = (value: string[]) => {
    setSelectedFields(value);
  };

  const handleSelectAll = () => {
    setSelectedFields(fields.map((field) => field.id));
  };

  const handleSelectNone = () => {
    setSelectedFields([]);
  };

  const handlePrint = () => {
    onClose();
    onPrint(selectedFields);
  };

  const groupedFields = fields.reduce(
    (acc, field) => {
      if (!acc[field.type]) {
        acc[field.type] = [];
      }
      acc[field.type].push(field);
      return acc;
    },
    {} as Record<string, FieldOption[]>,
  );

  return (
    <>
      <Button
        onClick={onOpen}
        colorPalette='brand'
        variant='outline'
        size='sm'
        className='no-print'
      >
        {buttonText}
        <Icon as={AdjustmentsHorizontalIcon} w={4} h={4} />
      </Button>

      <DialogRoot open={open} onOpenChange={(e) => setOpen(e.open)} size='md'>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Fields to Print</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <HStack mb={4} gap={4}>
              <Button size='sm' onClick={handleSelectAll} variant='outline'>
                Select All
              </Button>
              <Button size='sm' onClick={handleSelectNone} variant='outline'>
                Select None
              </Button>
              <Text color='fg.muted' fontSize='sm'>
                {selectedFields.length} of {fields.length} selected
              </Text>
            </HStack>

            <Separator mb={4} />

            <Stack gap={4} maxH='400px' overflowY='auto' pr={2}>
              {Object.entries(groupedFields).map(([type, typeFields]) => (
                <Box key={type}>
                  <Text fontWeight='medium' mb={2}>
                    {type}
                  </Text>
                  <CheckboxGroup
                    value={selectedFields}
                    onValueChange={handleToggleField}
                    name='selectedFields'
                    colorPalette='brand'
                  >
                    <Stack pl={2}>
                      {typeFields.map((field) => (
                        <Checkbox key={field.id} size='sm' value={field.id}>
                          {field.label}
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                </Box>
              ))}
            </Stack>
          </DialogBody>

          <DialogFooter>
            <Button variant='ghost' mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorPalette='brand'
              onClick={handlePrint}
              disabled={selectedFields.length === 0}
            >
              <Icon as={PrinterIcon} w={4} h={4} />
              Print Selected Fields
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </>
  );
}
